/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState, useCallback } from 'react'
import { Upload, Image } from 'antd'
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import req from '@sportninja/common/api/request'
import { Image as ImageType } from '@sportninja/common/types/Registration'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_MB,
} from '@sportninja/common/constants/imageRequirements'

interface PlayerPhotoSectionProps {
  formData: {
    image?: any
    imageUrl?: string
  }
  updateFormData: (field: string, value: string) => void
  teamId?: string
  playerId?: string
  disableFields?: boolean
  refetch: () => void
  playerImage?: ImageType | null
}

/**
 * 🔄 updatePlayerImage: Updates player image via API
 */
async function updatePlayerImage(
  teamId: string,
  playerId: string,
  base64: File
) {
  console.log('🔄 updatePlayerImage: Updating player image', {
    teamId,
    playerId,
  })

  try {
    const response = await req(`/teams/${teamId}/players/${playerId}/image`, {
      method: 'POST',
      body: base64,
    })
    return response
  } catch (error) {
    console.error('🔄 updatePlayerImage: Error updating player image', error)
    throw error
  }
}

/**
 * 🔄 getImageThumbnailId: Gets the thumbnail URL for player image
 */
function getImageThumbnailId(formData: any): string | null {
  if (!formData?.image) {
    return null
  }

  // Try to get thumbnail from formats
  if (formData.image.formats && formData.image.formats.length > 0) {
    return formData.image.formats[0].full_path
  }

  // Fallback to main image
  return formData.image.full_path || null
}

/**
 * 🔄 PlayerPhotoSection: Handles player photo upload and display
 *
 * This component manages player photo upload, display, and deletion functionality.
 * It maintains its own state for photo operations and integrates with the form data.
 */
const PlayerPhotoSection = ({
  formData,
  updateFormData,
  teamId,
  playerId,
  disableFields = false,
  refetch,
  playerImage,
}: PlayerPhotoSectionProps) => {
  const [, setNewPlayerImage] = useState<File | null>(null)
  const [newPlayerError, setNewPlayerError] = useState<string | null>(null)

  const beforeUpload = useCallback((file: File) => {
    const isAllowedType = ALLOWED_IMAGE_TYPES.includes(file.type)
    const isLtMaxSize = file.size / 1024 / 1024 <= MAX_IMAGE_SIZE_MB

    if (!isAllowedType) {
      setNewPlayerError(
        'Please upload a file in one of the following formats: PNG, JPG, GIF, WebP, AVIF, TIFF, or HEIC.'
      )
      return false
    }

    if (!isLtMaxSize) {
      setNewPlayerError(
        'File is too large. Please upload an image smaller than 5MB.'
      )
      return false
    }

    // Clear any previous errors
    setNewPlayerError(null)
    return true // Allow the upload
  }, [])

  const handleChange = useCallback(
    (info: any) => {
      if (info.file && info.file.originFileObj && teamId && playerId) {
        const file = info.file.originFileObj
        setNewPlayerImage(file)

        updatePlayerImage(teamId, playerId, file)
          .then(() => {
            refetch()
          })
          .catch((error) => {
            setNewPlayerError(
              error?.message ||
                'An error occurred while uploading the player photo'
            )
          })
      }
    },
    [teamId, playerId, refetch]
  )

  const handleDeleteImage = useCallback(async () => {
    if (!teamId || !playerId) {
      return
    }

    try {
      await req(`/teams/${teamId}/players/${playerId}/image`, {
        method: 'DELETE',
      })
      setNewPlayerImage(null)
      updateFormData('imageUrl', '')
      refetch()
    } catch (e: any) {
      setNewPlayerError(
        e?.message || 'An error occurred while deleting the player photo'
      )
    }
  }, [teamId, playerId, updateFormData, refetch])

  if (disableFields) {
    return null
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
        @media (min-width: 1080px) {
          width: 25%;
        }
      `}
    >
      {formData?.image || playerImage ? (
        <div
          css={css`
            display: flex;
            padding: 8px;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            border-radius: 8px;
            background-color: ${colors.SECONDARY_200};
            border: 1px solid ${colors.SECONDARY_100};
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <Image
              src={
                getImageThumbnailId(formData) ||
                getImageThumbnailId({
                  image: playerImage,
                }) ||
                '/images/generic_placeholders/placeholder_player.png'
              }
              alt='player photo'
              width={56}
              height={56}
              css={css`
                border-radius: 4px;
                object-fit: cover;
              `}
              preview={false}
            />
            <DeleteOutlined
              css={css`
                color: ${colors.ERROR_200};
                font-size: 24px;
                cursor: pointer;
              `}
              onClick={handleDeleteImage}
            />
          </div>
        </div>
      ) : (
        <Upload
          beforeUpload={beforeUpload}
          onChange={handleChange}
          showUploadList={false}
          action={undefined}
          css={css`
            .ant-upload-wrapper .ant-upload {
              width: 100% !important;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              padding: 8px;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              border-radius: 8px;
              border: 1px dashed ${colors.SECONDARY_100};
              cursor: pointer;
              width: 100%;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1 0 0;
              `}
            >
              <div
                css={css`
                  display: flex;
                  width: 48px;
                  height: 48px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                  background: ${colors.PRIMARY};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    width: 24px;
                    height: 24px;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    border-radius: 4px;
                    background: ${colors.WHITE};
                  `}
                >
                  <PlusCircleOutlined
                    css={css`
                      font-size: 24px;
                      color: ${colors.SECONDARY_300};
                      background-color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;
                  flex: 1 0 0;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='semiBold'
                  color={colors.NEUTRAL_0}
                >
                  Upload Player Photo
                </Text>
                <Text
                  variant='captionSmall'
                  weight='regular'
                  color={colors.NEUTRAL_200}
                >
                  PNG, JPG up to 5MB
                </Text>
              </div>
            </div>
          </div>
        </Upload>
      )}

      {newPlayerError && (
        <Text variant='captionSmall' weight='regular' color={colors.ERROR_200}>
          {newPlayerError}
        </Text>
      )}
    </div>
  )
}

export default PlayerPhotoSection
